// === GeneralStep.tsx ===
import { useForm, FormProvider } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { GeneralForm } from "./general-form"
import { GeneralFormData, generalSchema } from "@pma/utils/work-diaries-schema"
import { DictType } from "@pma/types"
import axios from "axios"
import {
  COMPANY_SETTINGS_ENDPOINT,
  SERVER_URL,
  WORK_DIARY_ADD_ENDPOINT,
  WORK_DIARY_EDIT_ENDPOINT,
} from "@pma/utils/api-routes"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { parseCookies } from "nookies"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "@pma/hooks/use-toast"
import useTranslator from "@pma/hooks/use-translator"
import { useEffect, useState } from "react"
import { Button } from "../../button"
import { useAuth } from "@pma/stores/auth-store"

interface GeneralStepProps {
  workDiaries: DictType | undefined
  project?: DictType
  lstStatus: any[]
  lstWeather: any[]
  lstContractor: any[]
  setActiveStep: React.Dispatch<React.SetStateAction<number>>
  setDiaryId?: React.Dispatch<React.SetStateAction<number>>
}

export function GeneralStep({
  lstContractor,
  lstWeather,
  lstStatus,
  workDiaries,
  project,
  setActiveStep,
  setDiaryId,
}: GeneralStepProps) {
  const { authConfig } = useAuth()
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const queryClient = useQueryClient()
  const { translate } = useTranslator()

  const orgId = authConfig?.last_organization_id

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLocationMandatory, setIsLocationMandatory] = useState(false)
  const [location, setLocation] = useState<{
    lat: number
    lon: number
  } | null>(null)

  const getOrganizationSettings = async () => {
    const response = await axios.get(
      SERVER_URL + COMPANY_SETTINGS_ENDPOINT + `?organization_id=${orgId}`,
      { headers: { Authorization: `Bearer ${accessToken}` } },
    )
    return response.data
  }

  const { data: settings } = useQuery({
    queryKey: ["company-settings", orgId],
    queryFn: getOrganizationSettings,
    enabled: !!orgId,
  })

  useEffect(() => {
    if (settings?.diary_location_mandatory) setIsLocationMandatory(true)
    else setIsLocationMandatory(false)
  }, [settings])

  useEffect(() => {
    if (!isLocationMandatory) return

    if (!navigator.geolocation) {
      console.warn("Geolocation not supported")
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          lat: position.coords.latitude,
          lon: position.coords.longitude,
        })
      },
      (error) => {
        console.error("Error getting location:", error)
        setLocation(null)
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      },
    )
  }, [isLocationMandatory])

  const form = useForm<GeneralFormData>({
    resolver: zodResolver(generalSchema(translate)),
    defaultValues: {
      weather: {
        description: 0,
        temperature: 0,
        rating: 0,
      },
      workDetails: [],
      number: "",
      date: new Date().toISOString(),
      siteAddress: "",
      projectName: project?.title,
      status: 1,
      description: "",
      company_id: 0,
    },
  })

  useEffect(() => {
    if (workDiaries) {
      form.reset({
        weather: {
          description: workDiaries?.weather_id,
          temperature: workDiaries?.temperature || 0,
          rating: workDiaries?.weather_rating,
        },
        workDetails: workDiaries?._work_details,
        company_id: workDiaries?.contractor_company_id || 0,
        number: workDiaries?.number || "",
        date: workDiaries?.diary_date || "",
        siteAddress: workDiaries?.address || "",
        projectName: project?.title || "",
        status: workDiaries?.status_id || 1,
        description: workDiaries?.description || "",
      })
    }
  }, [workDiaries, form, project?.title])

  const onSubmit = async (data: GeneralFormData) => {
    setIsSubmitting(true)

    if (isLocationMandatory && !location) {
      toast({
        title: "Location Required",
        description: "Please allow location access before submitting.",
        variant: "error",
      })
      setIsSubmitting(false)
      return
    }

    let dataFinal: any = {
      project_id: project?.id,
      date: data.date,
      weather_id: data.weather?.description,
      address: data.siteAddress,
      company_id: data?.company_id,
      status_id: data.status,
      temperature: data.weather?.temperature,
      weather_rating: data.weather?.rating,
      description: data.description,
      work_details: data.workDetails,
    }

    if (isLocationMandatory && location) {
      dataFinal.location_lat = location.lat
      dataFinal.location_lon = location.lon
    }

    if (workDiaries) {
      dataFinal.diary_id = workDiaries.id
    }

    try {
      const res = await addNewItem(dataFinal)

      if (setDiaryId) {
        setDiaryId(res.id)
      }

      toast({
        title: "Success",
        description: `${res.number} ${workDiaries
          ? translate("diary edited successfully")
          : translate("diary added successfully")
          }`,
        variant: "success",
      })

      await queryClient.invalidateQueries({
        queryKey: ["get-work-diary-details"],
      })
      await queryClient.invalidateQueries({
        queryKey: ["work-diaries-context"],
      })

      setActiveStep(1)
    } catch (error: any) {
      const message = error?.response?.data?.message

      toast({
        title: "Error",
        description: translate(message ?? '') || translate("Error occurred while creating diary"),
        variant: "error",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const addNewItem = async (body: DictType) => {
    const url = workDiaries
      ? SERVER_URL + WORK_DIARY_EDIT_ENDPOINT
      : SERVER_URL + WORK_DIARY_ADD_ENDPOINT

    const response = await axios.post(url, body, {
      headers: {
        Authorization: "Bearer " + accessToken,
        "Content-Type": "application/json",
      },
    })

    return response.data
  }

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-6'>
        <GeneralForm
          lstStatus={lstStatus}
          lstWeather={lstWeather}
          lstContractor={lstContractor}
          workDiaries={workDiaries}
          project={project}
        />

        <div className='flex flex-col justify-between gap-4 border-t pt-6 sm:flex-row'>
          <Button type='button' variant='outline' disabled className='w-full sm:w-auto'>
            {translate("Previous")}
          </Button>

          <Button
            type='submit'
            className='w-full sm:w-auto'
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            {`${translate("Save")} & ${translate("Next")}`}
          </Button>
        </div>
      </form>
    </FormProvider>
  )
}
